import React, { useEffect, useRef, useState } from "react";
import { Button } from "@mui/material";
import useGraphql from "../Hooks/useGraphql";
import { useTranslation } from "../context/Internationalization";
import { START_CLUB_BILLING_TRIAL } from "../graphql/billingGraphql";
import { billingIdentityErrorPresentation } from "../pages/Billing/billingIdentityErrors";

// Mounted with a club key so a late response cannot refresh a different club.
export default function StartClubTrialButton({ clubId, onStarted }) {
  const { t } = useTranslation();
  const { axiosGraphQL } = useGraphql();
  const [pending, setPending] = useState(false);
  const [errorKey, setErrorKey] = useState(null);
  const pendingRef = useRef(false);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const startTrial = async () => {
    if (pendingRef.current) return;
    pendingRef.current = true;
    setPending(true);
    setErrorKey(null);
    try {
      const response = await axiosGraphQL({
        query: START_CLUB_BILLING_TRIAL,
        variables: { clubId: String(clubId) },
      });
      const startedClub = response?.data?.data?.startClubBillingTrial?.clubId;
      if (String(startedClub) !== String(clubId)) throw new Error("missing_trial_status");
      if (mounted.current) await onStarted?.(String(clubId));
    } catch (error) {
      if (mounted.current) {
        const denied = error?.codes?.some(code => ["FORBIDDEN_BILLING_ACCESS", "FORBIDDEN_CLUB_ACCESS", "BILLING_ROLLOUT_DISABLED"].includes(code));
        setErrorKey(denied ? "billing_contact_administrator" :
          billingIdentityErrorPresentation(error?.codes, "billing_setup_error").key);
      }
    } finally {
      pendingRef.current = false;
      if (mounted.current) setPending(false);
    }
  };

  return <>
    <Button onClick={startTrial} disabled={pending} size="small">
      {t("billing_start_free_trial")}
    </Button>
    {errorKey && <div role="alert">{t(errorKey)}</div>}
  </>;
}
